import React, { useState } from "react";
import { Strategy } from "src/services/strategies.service";
import "../../../styles/components/forms/strategy-selector.css";
import { useFunnyMessages } from "src/hooks/useFunnyMessages";
import { CollapsibleFormSection } from "./CollapsibleFormSection";

interface StrategySelectorProps {
  strategies: Strategy[];
  isLoading: boolean;
  error: string | null;
  onGenerateStrategies: () => Promise<void>;
  onStrategyToggle: (strategy: string) => void;
  selectedStrategies: string[];
  smartGoal: string | null;
  className?: string;
}

export const StrategySelector: React.FC<StrategySelectorProps> = ({
  strategies,
  isLoading,
  error,
  onGenerateStrategies,
  onStrategyToggle,
  selectedStrategies,
  smartGoal,
  className,
}) => {
  const [showStrategies, setShowStrategies] = useState(false);

  const funnyMessage = useFunnyMessages(isLoading);

  const handleGenerateClick = async () => {
    if (smartGoal) {
      setShowStrategies(true);
      await onGenerateStrategies();
    }
  };

  const isStrategySelected = (strategyText: string) => {
    return selectedStrategies.includes(strategyText);
  };

  return (
    <CollapsibleFormSection title="Generate Strategies *" className={className}>
      {smartGoal ? (
        <>
          {/* Removed explanatory description to reduce redundancy */}

          {!showStrategies && (
            <div className="form-group center-button-container">
              <div className="generate-button-container">
                <div className="tooltip-container">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleGenerateClick}
                    disabled={isLoading || !smartGoal}
                  >
                    {isLoading ? funnyMessage : "Generate Strategies"}
                  </button>

                  {!smartGoal && (
                    <div className="tooltip">
                      <p>Please select a smart goal</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="form-error-message">
              <p>Error generating strategies: {error}</p>
            </div>
          )}

          {showStrategies && (
            <div className="strategies-container">
              {isLoading ? (
                <div className="loading-indicator">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeDasharray="32"
                      strokeDashoffset="10"
                    />
                  </svg>
                  <span>{funnyMessage}</span>
                </div>
              ) : (
                <>
                  <h3 className="strategies-subtitle">
                    Which strategies would you be happy to try?
                  </h3>
                  <p className="form-note strategies-note">
                    (You can always tweak things after download)
                  </p>

                  {strategies.length > 0 ? (
                    <div className="form-group">
                      {strategies.map((strategy) => (
                        <div
                          key={strategy.id}
                          className={`strategy-item ${
                            isStrategySelected(strategy.text) ? "selected" : ""
                          }`}
                          onClick={() => onStrategyToggle(strategy.text)}
                        >
                          <div className="checkbox-item">
                            <input
                              type="checkbox"
                              id={`strategy-${strategy.id}`}
                              name={`strategy-${strategy.id}`}
                              checked={isStrategySelected(strategy.text)}
                              onChange={(e) => {
                                e.stopPropagation();
                                onStrategyToggle(strategy.text);
                              }}
                            />
                            <div className="checkbox-custom"></div>
                            <label
                              className="checkbox-label"
                              htmlFor={`strategy-${strategy.id}`}
                            >
                              {strategy.text}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="form-note">
                      No strategies were generated. Try again or write your own
                      strategy below.
                    </p>
                  )}

                  <div className="form-group">
                    <div className="tooltip-container">
                      <button
                        type="button"
                        className="secondary-button regenerate-button"
                        onClick={() => onGenerateStrategies()}
                        disabled={isLoading}
                      >
                        Regenerate Strategies
                      </button>

                      {selectedStrategies.length < 1 && (
                        <div className="tooltip">
                          <p>Please select at least 1 strategy</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="form-note">
          Please select a SMART goal first to generate strategies.
        </p>
      )}
    </CollapsibleFormSection>
  );
};
